import ChatMpProvider from "@/core/data-provider/data-mp/chat/chat-mp-provider";
import PlayerMpProvider from "@/core/data-provider/data-mp/player/player-mp-provider.ts";
import { randomString } from "@/core/helpers/randomString.ts";
import { PlayerClient } from "@/core/rpc-mp-provider/messages/get-players.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import {
  ChatBrowserMessage,
  ChatBrowserPlayer,
  ChatMessage,
  ChatTab,
  ChatTarget,
  chatTargetTabMap,
} from "@/domains/chat/constants/chat.ts";

function decodeHTMLEntities(encodedStr: string): string {
  return encodedStr
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'");
}

export function getOutOfCharacterMessage(content: string): string | null {
  const result = content.match(/^\(\(\s(.*)\s\)\)$/);

  if (result === null) {
    return null;
  }

  return result[1];
}

export function getChatMessage(message: string): ChatMessage {
  const decodedHTML = decodeHTMLEntities(message);

  try {
    const m = JSON.parse(decodedHTML);

    // target faction player content payload

    if ("target" in m && "content" in m) {
      return m;
    }
  } catch (e) {
    //
  }

  return {
    target: ChatTarget.Native,
    faction: null,
    player: null,
    entities: [],
    content: String(decodedHTML),
    template: null,
    payload: null,
  };
}

export async function getChatBrowserMessage(
  chatMessage: ChatMessage,
): Promise<ChatBrowserMessage | null> {
  const chatTargetMessage = { ...chatMessage };
  let isOutOfCharacter: boolean = false;
  const outOfCharacterMessage = getOutOfCharacterMessage(chatMessage.content);
  if (outOfCharacterMessage !== null) {
    chatTargetMessage.content = outOfCharacterMessage;
    isOutOfCharacter = true;
  }

  let chatBrowserPlayer: ChatBrowserPlayer | null = null;
  if (chatMessage.player !== null) {
    const { playerId } = chatMessage.player;
    // const { characterId } = chatMessage.player;

    const playersResponse = await PlayerMpProvider.getPlayers([playerId], []);

    if (playersResponse instanceof RpcMpProviderSuccessResponse) {
      if (playerId in playersResponse.data) {
        const playerClient: PlayerClient = playersResponse.data[playerId];

        chatBrowserPlayer = {
          playerId: playerClient.playerId,
          characterId: playerClient.characterId,
          name: playerClient.name,
          know: playerClient.know,
          me: playerClient.me,
          distance: playerClient.distance,
          position: playerClient.position,
          heading: playerClient.heading,
          dimension: playerClient.dimension,
        };
      }
    }
  }

  return {
    uid: randomString(12),
    message: chatTargetMessage,
    player: chatBrowserPlayer,
    ooc: isOutOfCharacter,
    date: new Date(),
  };
}

export async function sendChatMessage(chatMessage: ChatMessage): Promise<void> {
  await ChatMpProvider.sendMessage(chatMessage);
}

export function getChatTargetTabs(chatTarget: ChatTarget): ChatTab[] {
  if (!(chatTarget in chatTargetTabMap)) {
    return [];
  }

  return chatTargetTabMap[chatTarget];
}

export function getSpeakingColor(distance: number): [number, number, number] {
  const minDistance = 6;
  const maxDistance = 20;

  const minValue = 230;
  const maxValue = 100;

  if (distance < minDistance) {
    return [minValue, minValue, minValue];
  }

  if (distance > 20) {
    return [maxValue, maxValue, maxValue];
  }

  const value =
    minValue - ((distance - minDistance) * (minValue - maxValue)) / (maxDistance - minDistance);

  return [value, value, value];
}

export function getHexColor(rgb: [number, number, number]): string {
  const [r, g, b] = rgb;

  return [
    "#",
    Math.round(r).toString(16),
    Math.round(g).toString(16),
    Math.round(b).toString(16),
  ].join("");
}
